/**
 * PATH: packages/ui/src/kanban/components/SubChainsPanel.tsx
 * FILE_ID: UI-KANBAN-SUBCHAINS-PANEL-001
 * PURPOSE: The CardDetailDrawer "Sub-chains" panel — renders the three
 *          related-card clusters (thread / site / topic) the BE recommends
 *          for the focal card, each with "Select these" (loads the board
 *          multi-selection) and "Convert sub-chain to notebook".
 * WHY: FEAT-2026-06-22-KANBAN-NOTEBOOKS Phase 3. Kept in a focused file (the
 *      drawer is already 2.5k lines, well over the ceiling) — the drawer
 *      mounts it with a one-line tag and threads its selection / convert
 *      handlers down. Self-hiding: renders nothing when the host predates
 *      `getRelatedCards` or every cluster is empty.
 * FLOW:
 *   getRelatedCards(cardId) ─▶ {thread,site,topic} ─▶ cluster cards
 *     ─▶ "Select these"  → onSelectCards([focal, ...ids])
 *     ─▶ "Convert"       → onConvertToNotebook([focal, ...ids])
 * DEPENDENCIES:
 *   - @tanstack/react-query: cached read of the related endpoint
 *   - ../api (KanbanApi): getRelatedCards
 *   - ../types (RelatedCardRow / RelatedSignal): wire rows
 */
import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  GitBranch,
  Loader2,
  MapPin,
  NotebookPen,
  Sparkles,
  SquareDashedMousePointer,
} from 'lucide-react'

import type { KanbanApi } from '../api'
import type { RelatedCardRow, RelatedSignal } from '../types'

export interface SubChainsPanelProps {
  api: KanbanApi
  /** Focal card whose sub-chains we recommend. */
  cardId: string
  /** "Select these" — replace the board selection with these ids. */
  onSelectCards?: (cardIds: string[]) => void
  /** "Convert sub-chain to notebook" — one-click Phase 1/2 create. */
  onConvertToNotebook?: (cardIds: string[]) => void | Promise<void>
}

const SIGNAL_META: Record<
  RelatedSignal,
  { label: string; hint: string; Icon: typeof GitBranch }
> = {
  thread: {
    label: 'Same email thread',
    hint: 'Cards on this card’s email conversation',
    Icon: GitBranch,
  },
  site: {
    label: 'Same site / job',
    hint: 'Cards on the same site or job',
    Icon: MapPin,
  },
  topic: {
    label: 'Similar topic',
    hint: 'Cards with similar content (vector match)',
    Icon: Sparkles,
  },
}

const SIGNAL_ORDER: RelatedSignal[] = ['thread', 'site', 'topic']

/** Compose the sub-chain id set: focal card first, then the cluster rows,
 *  deduped, order-preserving. The focal card is always part of its own
 *  sub-chain so a notebook built from it includes the card you opened. */
function subChainIds(focalId: string, rows: RelatedCardRow[]): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const id of [focalId, ...rows.map((r) => r.id)]) {
    if (!seen.has(id)) {
      seen.add(id)
      out.push(id)
    }
  }
  return out
}

function ClusterBlock({
  signal,
  rows,
  focalId,
  onSelectCards,
  onConvertToNotebook,
}: {
  signal: RelatedSignal
  rows: RelatedCardRow[]
  focalId: string
  onSelectCards?: (ids: string[]) => void
  onConvertToNotebook?: (ids: string[]) => void | Promise<void>
}) {
  const [converting, setConverting] = useState(false)
  const { label, hint, Icon } = SIGNAL_META[signal]
  const ids = useMemo(() => subChainIds(focalId, rows), [focalId, rows])

  const handleConvert = async () => {
    if (!onConvertToNotebook) return
    setConverting(true)
    try {
      await onConvertToNotebook(ids)
    } finally {
      setConverting(false)
    }
  }

  return (
    <div className="rounded-md border border-blue-200 bg-white px-2.5 py-2 dark:border-blue-900/60 dark:bg-zinc-900">
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <span
          className="flex items-center gap-1 text-[11px] font-semibold text-blue-700 dark:text-blue-300"
          title={hint}
        >
          <Icon className="h-3 w-3" aria-hidden />
          {label}
          <span className="font-normal text-blue-400">({rows.length})</span>
        </span>
        <div className="flex items-center gap-1">
          {onSelectCards && (
            <button
              type="button"
              onClick={() => onSelectCards(ids)}
              className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10.5px] font-medium text-blue-700 hover:bg-blue-100 dark:text-blue-300 dark:hover:bg-blue-900/40"
              title="Select these cards on the board"
            >
              <SquareDashedMousePointer className="h-3 w-3" aria-hidden />
              Select these
            </button>
          )}
          {onConvertToNotebook && (
            <button
              type="button"
              onClick={handleConvert}
              disabled={converting}
              className="inline-flex items-center gap-1 rounded bg-blue-600 px-1.5 py-0.5 text-[10.5px] font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              title="Convert this sub-chain into a notebook"
            >
              {converting ? (
                <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
              ) : (
                <NotebookPen className="h-3 w-3" aria-hidden />
              )}
              Notebook
            </button>
          )}
        </div>
      </div>
      <ul className="space-y-0.5">
        {rows.map((row) => (
          <li
            key={row.id}
            className="flex items-center gap-1.5 rounded px-1.5 py-0.5 text-[11.5px] text-neutral-700 dark:text-zinc-300"
          >
            <span className="flex-1 truncate">{row.title || '(untitled)'}</span>
            {row.board_name && (
              <span className="shrink-0 truncate text-[10px] text-neutral-400">
                {row.board_name}
              </span>
            )}
            {typeof row.distance === 'number' && (
              <span className="shrink-0 text-[10px] text-violet-500">
                {(1 - row.distance).toFixed(2)}
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}

export function SubChainsPanel({
  api,
  cardId,
  onSelectCards,
  onConvertToNotebook,
}: SubChainsPanelProps) {
  const relatedQ = useQuery({
    queryKey: ['kanban', 'card-related', cardId],
    queryFn: () => api.getRelatedCards!(cardId),
    enabled: !!cardId && typeof api.getRelatedCards === 'function',
    staleTime: 60_000,
  })

  // Host predates the endpoint → no panel at all (opt-out by omission).
  if (typeof api.getRelatedCards !== 'function') return null

  const data = relatedQ.data
  const nonEmpty = SIGNAL_ORDER.filter((s) => (data?.[s]?.length ?? 0) > 0)

  // Nothing to show (no signals) and not loading → stay out of the way.
  if (!relatedQ.isLoading && nonEmpty.length === 0) return null

  return (
    <section className="rounded-lg border border-blue-200 bg-blue-50/60 px-3 py-2.5 dark:border-blue-900 dark:bg-blue-950/30">
      <p className="mb-1.5 flex items-center gap-1 text-[11px] font-semibold text-blue-700 dark:text-blue-300">
        <GitBranch className="h-3 w-3" aria-hidden />
        Sub-chains
      </p>
      {relatedQ.isLoading ? (
        <div className="flex items-center gap-1.5 px-1 py-1 text-[11px] text-blue-500">
          <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
          Finding related cards…
        </div>
      ) : (
        <div className="space-y-2">
          {nonEmpty.map((signal) => (
            <ClusterBlock
              key={signal}
              signal={signal}
              rows={data![signal]}
              focalId={cardId}
              onSelectCards={onSelectCards}
              onConvertToNotebook={onConvertToNotebook}
            />
          ))}
        </div>
      )}
    </section>
  )
}
